import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import { useLanguage } from '../../contexts/LanguageContext'
import { useLanguageNavigation } from '../../hooks/useLanguageNavigation'
import ShareModal from './ShareModal'
import TagInput from './TagInput'
import TagList from './TagList'
import PetService from '../../services/petService'
import SuccessNotification from '../common/SuccessNotification'

const {
  FiCamera, FiUpload, FiCheck, FiX, FiInfo, FiEye, FiArrowLeft, FiHeart,
  FiClipboard, FiCalendar, FiShield, FiBookmark, FiStar, FiTarget,
  FiChevronDown, FiChevronUp, FiHelpCircle, FiSave, FiPlus, FiAlertCircle,
  FiCheckCircle, FiClock, FiAlertTriangle, FiMessageSquare, FiSettings,
  FiList, FiUser, FiFilter, FiRotateCw, FiFileText, FiTrash2, FiDownload,
  FiShare2, FiImage, FiPaperclip, FiSmile, FiArrowRight, FiHome,
  FiActivity, FiGift, FiDollarSign, FiPhone, FiMapPin, FiArrowUp, FiEdit3,
  FiMail
} = FiIcons

// 彩色輸入框組件（用於名稱、體重、身高等不需要 Tag 的欄位）
const ColoredInput = ({ value, onChange, placeholder, color = "blue", type = "text", className = "" }) => {
  const colorClasses = {
    blue: 'border-blue-300 focus:border-blue-500 focus:ring-blue-500 bg-blue-50/30',
    green: 'border-green-300 focus:border-green-500 focus:ring-green-500 bg-green-50/30',
    purple: 'border-purple-300 focus:border-purple-500 focus:ring-purple-500 bg-purple-50/30',
    orange: 'border-orange-300 focus:border-orange-500 focus:ring-orange-500 bg-orange-50/30',
    red: 'border-red-300 focus:border-red-500 focus:ring-red-500 bg-red-50/30',
    indigo: 'border-indigo-300 focus:border-indigo-500 focus:ring-indigo-500 bg-indigo-50/30',
    yellow: 'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500 bg-yellow-50/30',
    pink: 'border-pink-300 focus:border-pink-500 focus:ring-pink-500 bg-pink-50/30',
    teal: 'border-teal-300 focus:border-teal-500 focus:ring-teal-500 bg-teal-50/30',
  }

  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${colorClasses[color]} ${className}`}
    />
  )
}

const PetForm = ({ petId, isEdit = false }) => {
  const { navigateTo } = useLanguageNavigation()
  const { t } = useLanguage()
  const fileInputRef = useRef(null)
  const formContainerRef = useRef(null)
  const petService = new PetService()
  
  const [formState, setFormState] = useState({
    currentStep: 1,
    previewMode: false,
    showHelpTips: true,
    uploadingPhoto: false,
    showShareModal: false,
    loading: false,
    saving: false,
    savedPet: null,
    justSaved: false,
    saveMessage: '',
    autoSaveEnabled: false,
    errors: {}
  })
  
  const [expandedSections, setExpandedSections] = useState({
    physical: true,
    dietary: true,
    behavior: true,
    health: true,
    vet: true,
    special: true
  })
  
  const [successNotification, setSuccessNotification] = useState({
    isVisible: false,
    title: '',
    message: '',
    type: 'success',
    actions: []
  })
  
  const [birthDateComponents, setBirthDateComponents] = useState({
    year: '',
    month: '',
    day: ''
  })
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'dog',
    breed: '',
    birth_date: '', 
    gender: '',
    microchip_id: '',
    registration_id: '',
    photo_url: '',
    weight: '',
    height: '',
    color: '',
    coat_type: '',
    eye_color: '',
    body_size: '',
    special_markings: '',
    feeding_schedule: '', 
    feeding_plan: [], 
    portion_size: '',
    can_eat: '',
    cannot_eat: '',
    likes: '',
    dislikes: '',
    temperament: '',
    exercise_needs: '',
    training_notes: '',
    medications: '', 
    medication_schedule: '', 
    medication_plan: [], 
    vaccines: '',
    next_vaccine_date: '', 
    allergies: '',
    medical_conditions: '',
    vet_name: '', 
    vet_phone: '',
    vet_address: '',
    vet_email: '',
    insurance_provider: '',
    insurance_policy: '',
    special_notes: '',
    current_location: ''
  })

  const scrollToTop = () => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }, 10)
  }
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = currentYear; i >= currentYear - 50; i--) {
      years.push(i)
    }
    return years
  }

  const getMonthOptions = () => {
    return Array.from({ length: 12 }, (_, i) => i + 1)
  }

  const getDayOptions = () => {
    const { year, month } = birthDateComponents
    
    if (year && month) {
      const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate()
      return Array.from({ length: daysInMonth }, (_, i) => i + 1)
    }
    
    if (month && !year) {
      const daysInMonth = new Date(2024, parseInt(month), 0).getDate()
      return Array.from({ length: daysInMonth }, (_, i) => i + 1)
    }
    
    return Array.from({ length: 31 }, (_, i) => i + 1)
  }

  useEffect(() => {
    const { year, month, day } = birthDateComponents
    if (year && month && day) {
      const monthStr = month.toString().padStart(2, '0')
      const dayStr = day.toString().padStart(2, '0')
      const dateString = `${year}-${monthStr}-${dayStr}`
      
      if (dateString !== formData.birth_date) {
        setFormData(prev => ({ ...prev, birth_date: dateString }))
      }
    } else if (!year && !month && !day && formData.birth_date) {
      setFormData(prev => ({ ...prev, birth_date: '' }))
    }
  }, [birthDateComponents])

  useEffect(() => {
    if (formData.birth_date && formData.birth_date.includes('-')) {
      const [year, month, day] = formData.birth_date.split('-')
      setBirthDateComponents({
        year: year || '',
        month: month ? parseInt(month, 10).toString() : '',
        day: day ? parseInt(day, 10).toString() : ''
      })
    }
  }, [formData.birth_date])

  const calculateAge = (birthDate) => {
    if (!birthDate) return null
    const today = new Date()
    const birth = new Date(birthDate)
    if (birth > today) return null
    let years = today.getFullYear() - birth.getFullYear()
    let months = today.getMonth() - birth.getMonth()
    if (months < 0) { years--; months += 12 }
    if (today.getDate() < birth.getDate()) { months--; if (months < 0) { years--; months += 12 } }
    return { years, months }
  }

  const formatAge = (birthDate) => {
    const age = calculateAge(birthDate)
    if (!age) return t('petForm.unknown')
    if (age.years === 0) return age.months === 1 ? `1 ${t('petForm.month')}` : `${age.months} ${t('petForm.months')}`
    else if (age.months === 0) return age.years === 1 ? `1 ${t('petForm.year')}` : `${age.years} ${t('petForm.years')}`
    else return `${age.years} ${age.years === 1 ? t('petForm.year') : t('petForm.years')} ${age.months} ${age.months === 1 ? t('petForm.month') : t('petForm.months')}`
  }

  const updateFormState = (updates) => setFormState(prev => ({ ...prev, ...updates }))

  useEffect(() => {
    // ✅ 只有在編輯模式且有有效的 petId 時才載入資料
    if (isEdit && petId) {
      console.log('📝 編輯模式：載入寵物資料', petId)
      loadPetData()
    } else {
      console.log('✨ 新增模式：使用空白表單')
    }
  }, [isEdit, petId])

  const loadPetData = async () => {
    updateFormState({ loading: true })
    try {
      const pet = await petService.getPetById(petId)
      if (pet) {
        let birthDate = pet.birth_date
        if (!birthDate && pet.age) {
          const currentDate = new Date()
          const birthYear = currentDate.getFullYear() - Math.floor(pet.age)
          birthDate = `${birthYear}-01-01`
        }
        setFormData({
          ...pet,
          birth_date: birthDate || '',
          weight: pet.weight || '',
          height: pet.height || '',
          color: pet.color || '',
          coat_type: pet.coat_type || '',
          eye_color: pet.eye_color || '',
          body_size: pet.body_size || '',
          special_markings: pet.special_markings || '',
          microchip_id: pet.microchip_id || '',
          registration_id: pet.registration_id || '',
          photo_url: pet.photo_url || '',
          feeding_schedule: pet.feeding_schedule || '',
          feeding_plan: Array.isArray(pet.feeding_plan) ? pet.feeding_plan : [],
          portion_size: pet.portion_size || '',
          can_eat: pet.can_eat || '',
          cannot_eat: pet.cannot_eat || '',
          likes: pet.likes || '',
          dislikes: pet.dislikes || '',
          temperament: pet.temperament || '',
          exercise_needs: pet.exercise_needs || '',
          training_notes: pet.training_notes || '',
          medications: pet.medications || '',
          medication_schedule: pet.medication_schedule || '',
          medication_plan: Array.isArray(pet.medication_plan) ? pet.medication_plan : [],
          vaccines: pet.vaccines || '',
          next_vaccine_date: pet.next_vaccine_date || '',
          allergies: pet.allergies || '',
          medical_conditions: pet.medical_conditions || '',
          vet_name: pet.vet_name || '',
          vet_phone: pet.vet_phone || '',
          vet_address: pet.vet_address || '',
          vet_email: pet.vet_email || '',
          insurance_provider: pet.insurance_provider || '',
          insurance_policy: pet.insurance_policy || '',
          special_notes: pet.special_notes || '',
          current_location: pet.current_location || ''
        })
        updateFormState({ savedPet: pet })
      } else {
        setSuccessNotification({
          isVisible: true,
          title: t('petForm.petNotFound'),
          message: t('petForm.petNotFoundMessage'),
          type: 'error',
          actions: [
            {
              key: 'dashboard',
              label: t('petForm.backToDashboard'),
              variant: 'primary',
              icon: FiHome,
              onClick: () => {
                setSuccessNotification(prev => ({ ...prev, isVisible: false }))
                navigateTo('/dashboard')
              }
            }
          ]
        })
      }
    } catch (error) {
      console.error('❌ 載入錯誤:', error)
      setSuccessNotification({
        isVisible: true,
        title: t('petForm.errorOccurred'),
        message: error.message || t('petForm.loadFailed'),
        type: 'error',
        actions: [
          {
            key: 'dashboard',
            label: t('petForm.backToDashboard'),
            variant: 'primary',
            icon: FiHome,
            onClick: () => {
              setSuccessNotification(prev => ({ ...prev, isVisible: false }))
              navigateTo('/dashboard')
            }
          }
        ]
      })
    } finally {
      updateFormState({ loading: false })
    }
  }

  const totalSteps = 5
  
  const stepTitles = [
    t('petForm.basicDetails'),
    t('petForm.physicalTraits'),
    t('petForm.careNeeds'),
    t('petForm.healthRecords'),
    t('petForm.review')
  ]

  const validateForm = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = t('petForm.nameRequired')
    if (!formData.type) newErrors.type = t('petForm.typeRequired')
    if (formData.birth_date) {
      const birthDate = new Date(formData.birth_date)
      const today = new Date()
      if (birthDate > today) newErrors.birth_date = t('petForm.birthDateCannotBeFuture')
      const maxAge = 50
      const minBirthYear = today.getFullYear() - maxAge
      if (birthDate.getFullYear() < minBirthYear) newErrors.birth_date = t('petForm.birthDateTooOld', { maxAge })
    }
    updateFormState({ errors: newErrors })
    return Object.keys(newErrors).length === 0
  }

  const calculateCompletion = () => {
    const requiredFields = ['name', 'type', 'breed', 'birth_date']
    const optionalFields = [
        'gender', 'weight', 'color', 'can_eat', 'cannot_eat', 'likes', 'temperament', 
        'vaccines', 'vet_name', 'next_vaccine_date', 'current_location',
        'vet_address', 'vet_email', 'coat_type', 'eye_color', 'body_size', 'special_markings'
    ]
    const completedRequired = requiredFields.filter(field => formData[field]).length
    const completedOptional = optionalFields.filter(field => formData[field]).length
    const hasFeedingPlan = formData.feeding_plan && formData.feeding_plan.length > 0
    const hasMedsPlan = formData.medication_plan && formData.medication_plan.length > 0
    const extraPoints = (hasFeedingPlan ? 1 : 0) + (hasMedsPlan ? 1 : 0)
    const requiredPercentage = (completedRequired / requiredFields.length) * 60
    const optionalPercentage = ((completedOptional + extraPoints) / (optionalFields.length + 2)) * 40
    return Math.round(requiredPercentage + optionalPercentage)
  }

  const getCompletionLevel = () => {
    const completion = calculateCompletion()
    if (completion >= 80) return { level: t('petForm.excellent'), color: 'success' }
    if (completion >= 50) return { level: t('petForm.good'), color: 'warning' }
    return { level: t('petForm.minimal'), color: 'danger' }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (formState.errors[field]) updateFormState({ errors: { ...formState.errors, [field]: null } })
    if (formState.autoSaveEnabled && formState.savedPet && formState.savedPet.id) debouncedAutoSave()
  }

  const handleBirthDateComponentChange = (component, value) => {
    setBirthDateComponents(prev => {
      const newComponents = { ...prev, [component]: value }
      
      if (component === 'year' || component === 'month') {
        const { year, month, day } = newComponents
        if (year && month && day) {
          const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate()
          if (parseInt(day) > daysInMonth) {
            newComponents.day = daysInMonth.toString()
          }
        } else if (!year && month && day) {
          const daysInMonth = new Date(2024, parseInt(month), 0).getDate()
          if (parseInt(day) > daysInMonth) {
            newComponents.day = daysInMonth.toString()
          }
        }
      }
      
      return newComponents
    })
    
    if (formState.errors.birth_date) {
      updateFormState({ errors: { ...formState.errors, birth_date: null } })
    }
  }

  const addFeedingTime = () => {
    const newMeal = { id: Date.now(), time: '08:00', portion: '', food: '' }
    const updatedPlan = [...formData.feeding_plan, newMeal]
    handleInputChange('feeding_plan', updatedPlan)
    updateLegacyFeedingSchedule(updatedPlan)
  }

  const removeFeedingTime = (id) => {
    const updatedPlan = formData.feeding_plan.filter(item => item.id !== id)
    handleInputChange('feeding_plan', updatedPlan)
    updateLegacyFeedingSchedule(updatedPlan)
  }

  const updateFeedingItem = (id, field, value) => {
    const updatedPlan = formData.feeding_plan.map(item => item.id === id ? { ...item, [field]: value } : item)
    handleInputChange('feeding_plan', updatedPlan)
    updateLegacyFeedingSchedule(updatedPlan)
  }

  const updateLegacyFeedingSchedule = (plan) => {
    if (!plan || plan.length === 0) {
      handleInputChange('feeding_schedule', '')
      return
    }
    const summary = plan.sort((a, b) => a.time.localeCompare(b.time)).map(p => `${p.time} ${p.portion ? `(${p.portion})` : ''}`).join(', ')
    handleInputChange('feeding_schedule', summary)
  }

  const addMedication = () => {
    const newMed = { id: Date.now(), name: '', dosage: '', frequency: 'Daily', times: ['08:00'] }
    const updatedPlan = [...formData.medication_plan, newMed]
    handleInputChange('medication_plan', updatedPlan)
    updateLegacyMedicationSchedule(updatedPlan)
  }

  const removeMedication = (id) => {
    const updatedPlan = formData.medication_plan.filter(item => item.id !== id)
    handleInputChange('medication_plan', updatedPlan)
    updateLegacyMedicationSchedule(updatedPlan)
  }

  const updateMedicationItem = (id, field, value) => {
    const updatedPlan = formData.medication_plan.map(item => item.id === id ? { ...item, [field]: value } : item)
    handleInputChange('medication_plan', updatedPlan)
    updateLegacyMedicationSchedule(updatedPlan)
  }

  const addMedTime = (medId) => {
    const updatedPlan = formData.medication_plan.map(item => {
      if (item.id === medId) return { ...item, times: [...(item.times || []), '08:00'] }
      return item
    })
    handleInputChange('medication_plan', updatedPlan)
    updateLegacyMedicationSchedule(updatedPlan)
  }

  const removeMedTime = (medId, timeIndex) => {
    const updatedPlan = formData.medication_plan.map(item => {
      if (item.id === medId) {
        const newTimes = [...item.times]
        newTimes.splice(timeIndex, 1)
        return { ...item, times: newTimes }
      }
      return item
    })
    handleInputChange('medication_plan', updatedPlan)
    updateLegacyMedicationSchedule(updatedPlan)
  }

  const updateMedTime = (medId, timeIndex, value) => {
    const updatedPlan = formData.medication_plan.map(item => {
      if (item.id === medId) {
        const newTimes = [...item.times]
        newTimes[timeIndex] = value
        return { ...item, times: newTimes }
      }
      return item
    })
    handleInputChange('medication_plan', updatedPlan)
    updateLegacyMedicationSchedule(updatedPlan)
  }

  const updateLegacyMedicationSchedule = (plan) => {
    if (!plan || plan.length === 0) {
      handleInputChange('medication_schedule', '')
      handleInputChange('medications', '')
      return
    }
    const scheduleSummary = plan.map(m => {
        const times = m.times ? m.times.join(', ') : ''
        return `${m.name}: ${times}`
    }).join('; ')
    handleInputChange('medication_schedule', scheduleSummary)
    const medsSummary = plan.map(m => `${m.name} (${m.dosage})`).join(', ')
    handleInputChange('medications', medsSummary)
  }

  const debouncedAutoSave = (() => {
    let timeoutId
    return () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => { if (!formState.saving && formData.name.trim()) handleQuickSave(true) }, 2000)
    }
  })()

  const handleStepChange = (step) => {
    if (step >= 1 && step <= totalSteps) {
      updateFormState({ currentStep: step, previewMode: false })
      scrollToTop()
    }
  }

  const handleNext = () => {
    if (formState.currentStep < totalSteps) {
      updateFormState({ currentStep: formState.currentStep + 1 })
      scrollToTop()
    }
  }

  const handlePrevious = () => {
    if (formState.currentStep > 1) {
      updateFormState({ currentStep: formState.currentStep - 1 })
      scrollToTop()
    }
  }

  const handlePreviewToggle = () => updateFormState({ previewMode: !formState.previewMode })

  const handlePhotoUpload = async (file) => {
    if (!file) return
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
    if (!validTypes.includes(file.type)) { alert(t('petForm.invalidFileType')); return }
    if (file.size > 5 * 1024 * 1024) { alert(t('petForm.fileTooLarge')); return }
    
    updateFormState({ uploadingPhoto: true })
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = `pet-photos/${fileName}`
      
      const { data: uploadData, error: uploadError } = await petService.supabase.storage
        .from('pet-images')
        .upload(filePath, file, { cacheControl: '3600', upsert: false })
      
      if (uploadError) {
        const reader = new FileReader()
        reader.onload = (e) => {
          handleInputChange('photo_url', e.target.result)
          updateFormState({ uploadingPhoto: false, saveMessage: t('petForm.imageStoredLocally') })
          setTimeout(() => updateFormState({ saveMessage: '' }), 3000)
        }
        reader.readAsDataURL(file)
        return
      }
      
      const { data: urlData } = petService.supabase.storage.from('pet-images').getPublicUrl(filePath)
      if (urlData?.publicUrl) {
        handleInputChange('photo_url', urlData.publicUrl)
        updateFormState({ uploadingPhoto: false, saveMessage: t('petForm.imageUploadedSuccessfully') })
        setTimeout(() => updateFormState({ saveMessage: '' }), 3000)
      } else { throw new Error('無法獲取圖片 URL') }
    } catch (error) {
      console.error('❌ 圖片處理失敗:', error)
      const reader = new FileReader()
      reader.onload = (e) => {
        handleInputChange('photo_url', e.target.result)
        updateFormState({ uploadingPhoto: false, saveMessage: t('petForm.imageStoredAsBase64') })
        setTimeout(() => updateFormState({ saveMessage: '' }), 3000)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleQuickSave = async (isAutoSave = false) => {
    if (formState.saving) return 
    if (!formData.name.trim()) {
      updateFormState({ saveMessage: t('petForm.enterNameToSave') })
      setTimeout(() => updateFormState({ saveMessage: '' }), 3000)
      return
    }
    updateFormState({ saving: true })
    try {
      const petData = { ...formData, id: formState.savedPet?.id || (isEdit ? parseInt(petId) : undefined) }
      const savedPetResult = await petService.savePet(petData)
      if (savedPetResult && savedPetResult.id) {
        updateFormState({ savedPet: savedPetResult })
        if (savedPetResult.photo_url && savedPetResult.photo_url !== formData.photo_url) {
          setFormData(prev => ({ ...prev, photo_url: savedPetResult.photo_url }))
        }
        updateFormState({ saveMessage: isAutoSave ? t('petForm.autoSaved') : t('petForm.saved') })
        if (!isAutoSave) updateFormState({ autoSaveEnabled: true })
        setTimeout(() => updateFormState({ saveMessage: '' }), 3000)
      } else { throw new Error(t('petForm.saveDataError')) }
    } catch (error) {
      console.error('❌ 快速儲存失敗:', error)
      updateFormState({ saveMessage: t('petForm.saveFailed') })
      setTimeout(() => updateFormState({ saveMessage: '' }), 3000)
    } finally { updateFormState({ saving: false }) }
  }

  const handleSave = async () => {
    if (!validateForm()) {
      setSuccessNotification({ isVisible: true, title: t('petForm.fixFormErrors'), message: t('petForm.pleaseCheckAndFixErrors'), type: 'error', actions: [{ key: 'close', label: t('petForm.ok'), variant: 'primary', icon: FiCheck, onClick: () => setSuccessNotification(prev => ({ ...prev, isVisible: false })) }] })
      return
    }
    
    updateFormState({ saving: true })
    try {
      const petData = { ...formData, id: formState.savedPet?.id || (isEdit ? parseInt(petId) : undefined) }
      const savedPetResult = await petService.savePet(petData)
      
      if (savedPetResult && savedPetResult.id) {
        updateFormState({ savedPet: savedPetResult, justSaved: true, autoSaveEnabled: true })
        if (savedPetResult.photo_url && savedPetResult.photo_url !== formData.photo_url) {
          setFormData(prev => ({ ...prev, photo_url: savedPetResult.photo_url }))
        }
        
        setSuccessNotification({
          isVisible: true,
          title: isEdit ? t('petForm.petUpdatedSuccessfully') : t('petForm.petSavedSuccessfully'),
          message: t('petForm.nowYouCanShare'),
          type: 'success',
          actions: [
            { key: 'share', label: t('petForm.shareNow'), variant: 'primary', icon: FiShare2, onClick: () => { setSuccessNotification(prev => ({ ...prev, isVisible: false })); handleShowShareModal() } },
            { 
              key: 'dashboard', 
              label: t('petForm.backToDashboard'), 
              variant: 'secondary', 
              icon: FiHome, 
              onClick: () => { 
                setSuccessNotification(prev => ({ ...prev, isVisible: false }));
                petService.clearCache();
                navigateTo('/dashboard');
              } 
            },
            { key: 'continue', label: t('petForm.continueEditing'), variant: 'secondary', icon: FiEdit3, onClick: () => setSuccessNotification(prev => ({ ...prev, isVisible: false })) }
          ]
        })
      } else { throw new Error(t('petForm.saveDataError')) }
    } catch (error) {
      setSuccessNotification({ isVisible: true, title: t('petForm.errorOccurred'), message: error.message || t('petForm.saveFailed'), type: 'error', actions: [{ key: 'retry', label: t('petForm.retry'), variant: 'primary', icon: FiRotateCw, onClick: () => { setSuccessNotification(prev => ({ ...prev, isVisible: false })); handleSave() } }, { key: 'close', label: t('petForm.close'), variant: 'secondary', icon: FiX, onClick: () => setSuccessNotification(prev => ({ ...prev, isVisible: false })) }] })
    } finally { updateFormState({ saving: false }) }
  }

  const handleShowShareModal = () => {
    if (formState.savedPet && formState.savedPet.share_token) updateFormState({ showShareModal: true })
    else if (isEdit && formData.id) updateFormState({ showShareModal: true })
    else setSuccessNotification({ isVisible: true, title: t('petForm.saveBeforeShare'), message: t('petForm.needToSaveBeforeSharing'), type: 'info', actions: [{ key: 'saveAndShare', label: t('petForm.saveAndShare'), variant: 'primary', icon: FiSave, onClick: () => { setSuccessNotification(prev => ({ ...prev, isVisible: false })); handleSaveAndShare() } }, { key: 'cancel', label: t('petForm.cancel'), variant: 'secondary', icon: FiX, onClick: () => setSuccessNotification(prev => ({ ...prev, isVisible: false })) }] })
  }

  const handleSaveAndShare = async () => {
    if (!validateForm()) {
      setSuccessNotification({ isVisible: true, title: t('petForm.fixFormErrors'), message: t('petForm.pleaseCheckAndFixErrors'), type: 'error', actions: [{ key: 'close', label: t('petForm.ok'), variant: 'primary', icon: FiCheck, onClick: () => setSuccessNotification(prev => ({ ...prev, isVisible: false })) }] })
      return
    }
    updateFormState({ saving: true })
    try {
      const petData = { ...formData, id: formState.savedPet?.id || (isEdit ? parseInt(petId) : undefined) }
      const savedPetResult = await petService.savePet(petData)
      if (savedPetResult && savedPetResult.id) {
        updateFormState({ savedPet: savedPetResult, justSaved: true, autoSaveEnabled: true, showShareModal: true })
        if (savedPetResult.photo_url && savedPetResult.photo_url !== formData.photo_url) setFormData(prev => ({ ...prev, photo_url: savedPetResult.photo_url }))
      } else { throw new Error(t('petForm.saveDataError')) }
    } catch (error) {
      setSuccessNotification({ isVisible: true, title: t('petForm.errorOccurred'), message: error.message || t('petForm.saveFailed'), type: 'error', actions: [{ key: 'retry', label: t('petForm.retry'), variant: 'primary', icon: FiRotateCw, onClick: () => { setSuccessNotification(prev => ({ ...prev, isVisible: false })); handleSaveAndShare() } }, { key: 'close', label: t('petForm.close'), variant: 'secondary', icon: FiX, onClick: () => setSuccessNotification(prev => ({ ...prev, isVisible: false })) }] })
    } finally { updateFormState({ saving: false }) }
  }

  const handleBackToDashboard = () => navigateTo('/dashboard')
  const canSaveCurrentStep = () => {
    switch (formState.currentStep) {
      case 1: case 2: case 3: case 4: case 5: return formData.name.trim() !== ''
      default: return false
    }
  }

  const getPetTypeOptions = () => [
    { value: 'dog', label: t('common.species.dog'), icon: '🐕', description: t('common.loyalCompanion') },
    { value: 'cat', label: t('common.species.cat'), icon: '🐱', description: t('common.independentPartner') },
    { value: 'bird', label: t('common.species.bird'), icon: '🦜', description: t('common.cleverSinger') },
    { value: 'fish', label: t('common.species.fish'), icon: '🐠', description: t('common.peacefulWatcher') },
    { value: 'rabbit', label: t('common.species.rabbit'), icon: '🐰', description: t('common.gentleFriend') },
    { value: 'hamster', label: t('common.species.hamster'), icon: '🐹', description: t('common.livelySprite') },
    { value: 'turtle', label: t('common.species.turtle'), icon: '🐢', description: t('common.wiseElder') },
    { value: 'other', label: t('common.species.other'), icon: '🐾', description: t('common.specialPartner') }
  ]

  const PetTypeSelector = () => {
    const petTypeOptions = getPetTypeOptions()
    return (
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700 mb-4">{t('petForm.type')} *</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {petTypeOptions.map((option) => (
            <motion.div key={option.value} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className={`relative cursor-pointer rounded-2xl border-2 p-6 transition-all duration-300 ${formData.type === option.value ? 'border-indigo-500 bg-indigo-50 shadow-lg ring-2 ring-indigo-200' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'}`} onClick={() => handleInputChange('type', option.value)}>
              <div className="text-center">
                <div className="text-4xl mb-3">{option.icon}</div>
                <h3 className={`font-semibold text-sm ${formData.type === option.value ? 'text-indigo-900' : 'text-gray-900'}`}>{option.label}</h3>
                <p className={`text-xs mt-1 ${formData.type === option.value ? 'text-indigo-600' : 'text-gray-500'}`}>{option.description}</p>
              </div>
              {formData.type === option.value && (<div className="absolute top-2 right-2 w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center"><SafeIcon icon={FiCheck} className="h-4 w-4 text-white" /></div>)}
            </motion.div>
          ))}
        </div>
        {formState.errors.type && (<p className="text-red-500 text-sm mt-1">{formState.errors.type}</p>)}
      </div>
    )
  }

  const BirthDateSelector = () => {
    const yearOptions = getYearOptions()
    const monthOptions = getMonthOptions()
    const dayOptions = getDayOptions()

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('petForm.birthDate')} *
        </label>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('petForm.yearLabel')}</label>
            <select
              value={birthDateComponents.year}
              onChange={(e) => handleBirthDateComponentChange('year', e.target.value)}
              className={`w-full px-3 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all border-blue-300 focus:border-blue-500 focus:ring-blue-500 bg-blue-50/30 ${formState.errors.birth_date ? 'border-red-500' : ''}`}
            >
              <option value="">{t('petForm.selectYear')}</option>
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('petForm.monthLabel')}</label>
            <select
              value={birthDateComponents.month}
              onChange={(e) => handleBirthDateComponentChange('month', e.target.value)}
              className={`w-full px-3 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all border-blue-300 focus:border-blue-500 focus:ring-blue-500 bg-blue-50/30 ${formState.errors.birth_date ? 'border-red-500' : ''}`}
            >
              <option value="">{t('petForm.selectMonth')}</option>
              {monthOptions.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('petForm.dayLabel')}</label>
            <select
              value={birthDateComponents.day}
              onChange={(e) => handleBirthDateComponentChange('day', e.target.value)}
              className={`w-full px-3 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all border-blue-300 focus:border-blue-500 focus:ring-blue-500 bg-blue-50/30 ${formState.errors.birth_date ? 'border-red-500' : ''}`}
            >
              <option value="">{t('petForm.selectDay')}</option>
              {dayOptions.map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>
        </div>
        
        {formState.errors.birth_date && (
          <p className="text-red-500 text-sm mt-1">{formState.errors.birth_date}</p>
        )}
        
        {formData.birth_date && !formState.errors.birth_date && (
          <p className="mt-2 text-sm text-blue-600">
            {t('petForm.currentAge')}: {formatAge(formData.birth_date)}
          </p>
        )}
      </div>
    )
  }

  const StepSaveButton = () => {
    if (!canSaveCurrentStep()) return (<div className="text-center py-4"><p className="text-sm text-gray-500 mb-2">{formState.currentStep === 1 ? t('petForm.enterNameToSave') : t('petForm.fillBasicInfoToSave')}</p></div>)
    return (
      <div className="text-center py-4 border-t border-gray-100 mt-6">
        <div className="flex items-center justify-center space-x-4">
          <button onClick={() => handleQuickSave(false)} disabled={formState.saving} className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${formState.saving ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}><SafeIcon icon={formState.saving ? FiRotateCw : FiSave} className={`h-4 w-4 ${formState.saving ? 'animate-spin' : ''}`} /><span>{formState.saving ? t('petForm.saving') : t('petForm.saveProgress')}</span></button>
          {formState.autoSaveEnabled && (<div className="flex items-center space-x-2 text-sm"><SafeIcon icon={FiCheckCircle} className="h-4 w-4 text-green-500" /><span className="text-green-600">{t('petForm.autoSaveEnabled')}</span></div>)}
        </div>
        {formState.saveMessage && (<motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="mt-2"><span className={`text-sm px-3 py-1 rounded-full ${formState.saveMessage.includes('失敗') || formState.saveMessage.includes('Failed') ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>{formState.saveMessage}</span></motion.div>)}
      </div>
    )
  }

  const renderStepContent = () => {
    const petTypeOptions = getPetTypeOptions()
    switch (formState.currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('petForm.basicDetails')}</h3>
              <p className="text-gray-600">{t('petForm.basicInfoTips')}</p>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className={`w-32 h-32 rounded-full border-4 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors ${formState.uploadingPhoto ? 'opacity-50' : ''}`} onClick={() => fileInputRef.current?.click()}>
                  {formData.photo_url ? (<img src={formData.photo_url} alt={formData.name || t('petForm.petPhoto')} className="w-full h-full rounded-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />) : null}
                  <div className={`text-center ${formData.photo_url ? 'hidden' : 'flex'} flex-col items-center justify-center w-full h-full`}><SafeIcon icon={formState.uploadingPhoto ? FiRotateCw : FiCamera} className={`h-8 w-8 text-gray-400 mx-auto mb-2 ${formState.uploadingPhoto ? 'animate-spin' : ''}`} /><span className="text-sm text-gray-500">{formState.uploadingPhoto ? t('petForm.uploading') : t('petForm.petPhoto')}</span></div>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e.target.files[0])} className="hidden" />
              </div>
            </div>
            <PetTypeSelector />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.name')} *</label>
                <ColoredInput
                  value={formData.name}
                  onChange={(value) => handleInputChange('name', value)}
                  placeholder={t('petForm.enterPetName')}
                  color="indigo"
                  className={formState.errors.name ? 'border-red-500' : ''}
                />
                {formState.errors.name && (<p className="text-red-500 text-sm mt-1">{formState.errors.name}</p>)}
                {formState.showHelpTips && !formState.errors.name && (<p className="mt-1 text-xs text-gray-500">{t('petForm.nameHelpText')}</p>)}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.breed')} *</label>
                <ColoredInput
                  value={formData.breed}
                  onChange={(value) => handleInputChange('breed', value)}
                  placeholder={t('petForm.specificBreed')}
                  color="purple"
                />
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.breedHelpText')}</p>)}
              </div>
              
              <div className="md:col-span-2">
                <BirthDateSelector />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.gender')}</label>
                <select 
                  value={formData.gender} 
                  onChange={(e) => handleInputChange('gender', e.target.value)} 
                  className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all border-pink-300 focus:border-pink-500 focus:ring-pink-500 bg-pink-50/30"
                >
                  <option value="">{t('petForm.notSpecified')}</option>
                  <option value="male">{t('petForm.male')}</option>
                  <option value="female">{t('petForm.female')}</option>
                </select>
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.genderHelpText')}</p>)}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.weight')}</label>
                <ColoredInput
                  value={formData.weight}
                  onChange={(value) => handleInputChange('weight', value)}
                  placeholder={t('petForm.enterWeight')}
                  color="green"
                />
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.weightHelpText')}</p>)}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.microchipId')}</label>
                <ColoredInput
                  value={formData.microchip_id}
                  onChange={(value) => handleInputChange('microchip_id', value)}
                  placeholder={t('petForm.enterMicrochipId')}
                  color="teal"
                />
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.microchipHelpText')}</p>)}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.registrationId')}</label>
                <ColoredInput
                  value={formData.registration_id}
                  onChange={(value) => handleInputChange('registration_id', value)}
                  placeholder={t('petForm.registrationId')}
                  color="yellow"
                />
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.registrationIdHelpText')}</p>)}
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <SafeIcon icon={FiMapPin} className="mr-2 text-red-500" />
                  {t('petForm.currentLocation')}
                </label>
                <ColoredInput
                  value={formData.current_location}
                  onChange={(value) => handleInputChange('current_location', value)}
                  placeholder={t('petForm.currentLocation')}
                  color="red"
                />
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.currentLocationHelpText')}</p>)}
              </div>
            </div>
            <StepSaveButton />
          </div>
        )
      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center"><h3 className="text-2xl font-bold text-gray-900 mb-2">{t('petForm.physicalTraits')}</h3><p className="text-gray-600">{t('petForm.physicalCharacteristicsDesc')}</p></div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.height')}</label>
                <ColoredInput
                  value={formData.height}
                  onChange={(value) => handleInputChange('height', value)}
                  placeholder={t('petForm.enterHeight')}
                  color="orange"
                />
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.heightHelpText')}</p>)}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.color')}</label>
                <TagInput 
                  value={formData.color} 
                  onChange={(value) => handleInputChange('color', value)} 
                  placeholder={t('petForm.colorExample')}
                  color="indigo"
                  showOperationHint={formState.showHelpTips}
                />
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.colorHint')}</p>)}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.coatType')}</label>
                <TagInput 
                  value={formData.coat_type} 
                  onChange={(value) => handleInputChange('coat_type', value)} 
                  placeholder={t('petForm.coatType')}
                  color="purple"
                  showOperationHint={formState.showHelpTips}
                />
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.coatTypeHint')}</p>)}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.eyeColor')}</label>
                <TagInput 
                  value={formData.eye_color} 
                  onChange={(value) => handleInputChange('eye_color', value)} 
                  placeholder={t('petForm.eyeColor')}
                  color="teal"
                  showOperationHint={formState.showHelpTips}
                />
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.eyeColorHint')}</p>)}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.bodySize')}</label>
                <TagInput 
                  value={formData.body_size} 
                  onChange={(value) => handleInputChange('body_size', value)} 
                  placeholder={t('petForm.bodySize')}
                  color="blue"
                  showOperationHint={formState.showHelpTips}
                />
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.bodySizeHint')}</p>)}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.specialMarkings')}</label>
                <TagInput 
                  value={formData.special_markings} 
                  onChange={(value) => handleInputChange('special_markings', value)} 
                  placeholder={t('petForm.specialMarkings')}
                  color="pink"
                  showOperationHint={formState.showHelpTips}
                />
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.specialMarkingsHint')}</p>)}
              </div>
            </div>
            <StepSaveButton />
          </div>
        )
      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center"><h3 className="text-2xl font-bold text-gray-900 mb-2">{t('petForm.careNeeds')}</h3><p className="text-gray-600">{t('petForm.dietaryTips')}</p></div>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('petForm.dietaryInfo')}</h4>
                <div className="mb-6 bg-blue-50 p-4 rounded-xl border border-blue-100">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-blue-900 flex items-center"><SafeIcon icon={FiCalendar} className="mr-2 text-blue-500" />{t('petForm.feedingSchedule')} ({t('dashboard.nextFeeding')})</label>
                    <button onClick={addFeedingTime} className="flex items-center text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors"><SafeIcon icon={FiPlus} className="mr-1 h-3 w-3" />{t('petForm.addMeal')}</button>
                  </div>
                  <div className="space-y-3">
                    {formData.feeding_plan && formData.feeding_plan.length > 0 ? (
                      formData.feeding_plan.map((meal) => (
                        <div key={meal.id} className="flex flex-wrap md:flex-nowrap items-center gap-3 bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                          <div className="w-full md:w-auto">
                            <span className="text-xs text-gray-500 block mb-1">{t('petForm.time')}</span>
                            <input type="time" value={meal.time} onChange={(e) => updateFeedingItem(meal.id, 'time', e.target.value)} className="w-full md:w-32 p-2 border border-gray-300 rounded-md text-sm" />
                            {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-400">{t('petForm.timeHint')}</p>)}
                          </div>
                          <div className="flex-1 min-w-[120px]">
                            <span className="text-xs text-gray-500 block mb-1">{t('petForm.portionSize')}</span>
                            <input type="text" value={meal.portion} onChange={(e) => updateFeedingItem(meal.id, 'portion', e.target.value)} placeholder={t('petForm.portionSizeExample')} className="w-full p-2 border border-gray-300 rounded-md text-sm" />
                            {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-400">{t('petForm.portionSizeHint')}</p>)}
                          </div>
                          <div className="flex-1 min-w-[120px]">
                            <span className="text-xs text-gray-500 block mb-1">{t('petForm.foodType')}</span>
                            <input type="text" value={meal.food} onChange={(e) => updateFeedingItem(meal.id, 'food', e.target.value)} placeholder={t('petForm.foodType')} className="w-full p-2 border border-gray-300 rounded-md text-sm" />
                            {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-400">{t('petForm.foodTypeHint')}</p>)}
                          </div>
                          <button onClick={() => removeFeedingTime(meal.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-full mt-4 md:mt-0" title={t('common.delete')}><SafeIcon icon={FiTrash2} className="h-4 w-4" /></button>
                        </div>
                      ))
                    ) : (<div className="text-center py-4 text-gray-500 text-sm bg-white rounded-lg border border-dashed border-gray-300">{t('petForm.noMealsAdded')}</div>)}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.canEat')}</label>
                    <TagInput 
                      value={formData.can_eat} 
                      onChange={(value) => handleInputChange('can_eat', value)} 
                      placeholder={t('petForm.canEatHelpText')}
                      color="green"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.canEatHint')}</p>)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.cannotEat')}</label>
                    <TagInput 
                      value={formData.cannot_eat} 
                      onChange={(value) => handleInputChange('cannot_eat', value)} 
                      placeholder={t('petForm.cannotEatHelpText')}
                      color="red"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.cannotEatHint')}</p>)}
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('petForm.behaviorInfo')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.likes')}</label>
                    <TagInput 
                      value={formData.likes} 
                      onChange={(value) => handleInputChange('likes', value)} 
                      placeholder={t('petForm.whatPetEnjoys')}
                      color="pink"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.likesHint')}</p>)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.dislikes')}</label>
                    <TagInput 
                      value={formData.dislikes} 
                      onChange={(value) => handleInputChange('dislikes', value)} 
                      placeholder={t('petForm.whatMakesPetUncomfortable')}
                      color="orange"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.dislikesHint')}</p>)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.temperament')}</label>
                    <TagInput 
                      value={formData.temperament} 
                      onChange={(value) => handleInputChange('temperament', value)} 
                      placeholder={t('petForm.temperamentExample')}
                      color="yellow"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.temperamentHint')}</p>)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.exerciseNeeds')}</label>
                    <TagInput 
                      value={formData.exercise_needs} 
                      onChange={(value) => handleInputChange('exercise_needs', value)} 
                      placeholder={t('petForm.exerciseExample')}
                      color="teal"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.exerciseNeedsHint')}</p>)}
                  </div>
                </div>
              </div>
            </div>
            <StepSaveButton />
          </div>
        )
      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center"><h3 className="text-2xl font-bold text-gray-900 mb-2">{t('petForm.healthRecords')}</h3><p className="text-gray-600">{t('petForm.healthTips')}</p></div>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('petForm.healthInfo')}</h4>
                <div className="mb-6 bg-purple-50 p-4 rounded-xl border border-purple-100">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-purple-900 flex items-center"><SafeIcon icon={FiClock} className="mr-2 text-purple-500" />{t('petForm.medicationSchedule')}</label>
                    <button onClick={addMedication} className="flex items-center text-xs bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors"><SafeIcon icon={FiPlus} className="mr-1 h-3 w-3" />{t('petForm.addMedication')}</button>
                  </div>
                  <div className="space-y-4">
                    {formData.medication_plan && formData.medication_plan.length > 0 ? (
                      formData.medication_plan.map((med) => (
                        <div key={med.id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 relative">
                          <button onClick={() => removeMedication(med.id)} className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50" title={t('common.delete')}><SafeIcon icon={FiX} className="h-4 w-4" /></button>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                            <div>
                              <span className="text-xs text-gray-500 block mb-1">{t('petForm.medicationName')}</span>
                              <input type="text" value={med.name} onChange={(e) => updateMedicationItem(med.id, 'name', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm" placeholder={t('petForm.medicationPlaceholder')} />
                              {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-400">{t('petForm.medicationNameHint')}</p>)}
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 block mb-1">{t('petForm.dosage')}</span>
                              <input type="text" value={med.dosage} onChange={(e) => updateMedicationItem(med.id, 'dosage', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm" placeholder={t('petForm.dosagePlaceholder')} />
                              {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-400">{t('petForm.dosageHint')}</p>)}
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 block mb-1">{t('petForm.frequency')}</span>
                              <select value={med.frequency} onChange={(e) => updateMedicationItem(med.id, 'frequency', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm">
                                <option value="Daily">{t('petForm.daily')}</option>
                                <option value="BID">{t('petForm.twiceDaily')}</option>
                                <option value="TID">{t('petForm.thriceDaily')}</option>
                                <option value="Weekly">{t('petForm.weekly')}</option>
                                <option value="Monthly">{t('petForm.monthly')}</option>
                                <option value="Custom">{t('petForm.custom')}</option>
                              </select>
                              {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-400">{t('petForm.frequencyHint')}</p>)}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                             <div className="flex items-center justify-between mb-2">
                               <span className="text-xs font-medium text-gray-600">{t('petForm.dosageTimes')}</span>
                               <button onClick={() => addMedTime(med.id)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center"><SafeIcon icon={FiPlus} className="w-3 h-3 mr-1" /> {t('petForm.addTime')}</button>
                             </div>
                             {formState.showHelpTips && (<p className="mb-2 text-xs text-gray-400">{t('petForm.dosageTimesHint')}</p>)}
                             <div className="flex flex-wrap gap-2">
                                {med.times && med.times.map((time, idx) => (
                                  <div key={idx} className="flex items-center bg-white border border-gray-200 rounded px-2 py-1">
                                    <input type="time" value={time} onChange={(e) => updateMedTime(med.id, idx, e.target.value)} className="text-sm border-none p-0 focus:ring-0 w-20" />
                                    <button onClick={() => removeMedTime(med.id, idx)} className="ml-2 text-gray-400 hover:text-red-500"><SafeIcon icon={FiX} className="h-3 w-3" /></button>
                                  </div>
                                ))}
                             </div>
                          </div>
                        </div>
                      ))
                    ) : (<div className="text-center py-4 text-gray-500 text-sm bg-white rounded-lg border border-dashed border-gray-300">{t('petForm.noMedicationsAdded')}</div>)}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.vaccines')}</label>
                    <TagInput 
                      value={formData.vaccines} 
                      onChange={(value) => handleInputChange('vaccines', value)} 
                      placeholder={t('petForm.vaccinesHelpText')}
                      color="blue"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.vaccinesHint')}</p>)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <SafeIcon icon={FiCalendar} className="mr-2 text-green-500" />
                      {t('petForm.nextVetVisit')}
                    </label>
                    <ColoredInput
                      type="date"
                      value={formData.next_vaccine_date}
                      onChange={(value) => handleInputChange('next_vaccine_date', value)}
                      placeholder=""
                      color="green"
                      className="min-h-[48px]"
                    />
                    {formState.showHelpTips && (<p className="text-xs text-gray-500 mt-1">{t('petForm.nextVetVisitHelp')}</p>)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.allergies')}</label>
                    <TagInput 
                      value={formData.allergies} 
                      onChange={(value) => handleInputChange('allergies', value)} 
                      placeholder={t('petForm.knownAllergies')}
                      color="orange"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.allergiesHint')}</p>)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.medicalConditions')}</label>
                    <TagInput 
                      value={formData.medical_conditions} 
                      onChange={(value) => handleInputChange('medical_conditions', value)} 
                      placeholder={t('petForm.chronicConditions')}
                      color="red"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.medicalConditionsHint')}</p>)}
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('petForm.veterinarianInfo')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center"><SafeIcon icon={FiMapPin} className="mr-2 text-orange-500" />{t('petForm.vetName')}</label>
                    <TagInput 
                      value={formData.vet_name} 
                      onChange={(value) => handleInputChange('vet_name', value)} 
                      placeholder={t('petForm.yourPetsVet')}
                      color="orange"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.vetNameHint')}</p>)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.vetPhone')}</label>
                    <TagInput 
                      value={formData.vet_phone} 
                      onChange={(value) => handleInputChange('vet_phone', value)} 
                      placeholder={t('petForm.emergencyContactPhone')}
                      color="red"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.vetPhoneHint')}</p>)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.vetAddress')}</label>
                    <TagInput 
                      value={formData.vet_address} 
                      onChange={(value) => handleInputChange('vet_address', value)} 
                      placeholder={t('petForm.enterVetAddress')}
                      color="blue"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.vetAddressHint')}</p>)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.vetEmail')}</label>
                    <TagInput 
                      value={formData.vet_email} 
                      onChange={(value) => handleInputChange('vet_email', value)} 
                      placeholder={t('petForm.enterVetEmail')}
                      color="purple"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.vetEmailHint')}</p>)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.insuranceProvider')}</label>
                    <TagInput 
                      value={formData.insurance_provider} 
                      onChange={(value) => handleInputChange('insurance_provider', value)} 
                      placeholder={t('petForm.insuranceProvider')}
                      color="blue"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.insuranceProviderHint')}</p>)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.insurancePolicy')}</label>
                    <TagInput 
                      value={formData.insurance_policy} 
                      onChange={(value) => handleInputChange('insurance_policy', value)} 
                      placeholder={t('petForm.insurancePolicy')}
                      color="purple"
                      showOperationHint={formState.showHelpTips}
                    />
                    {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.insurancePolicyHint')}</p>)}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('petForm.specialNotes')}</label>
                <TagInput 
                  value={formData.special_notes} 
                  onChange={(value) => handleInputChange('special_notes', value)} 
                  placeholder={t('petForm.additionalInfo')}
                  color="yellow"
                  showOperationHint={formState.showHelpTips}
                />
                {formState.showHelpTips && (<p className="mt-1 text-xs text-gray-500">{t('petForm.specialNotesHint')}</p>)}
              </div>
            </div>
            <StepSaveButton />
          </div>
        )
      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center"><h3 className="text-2xl font-bold text-gray-900 mb-2">{t('petForm.review')}</h3><p className="text-gray-600">{t('petForm.reviewDescription')}</p></div>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4"><h4 className="text-lg font-semibold text-gray-900">{t('petForm.profileCompletion')}</h4><span className={`px-3 py-1 rounded-full text-sm font-medium ${getCompletionLevel().color === 'success' ? 'bg-green-100 text-green-800' : getCompletionLevel().color === 'warning' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{getCompletionLevel().level}</span></div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2"><div className="bg-gradient-to-r from-blue-500 to-indigo-500 h-3 rounded-full transition-all duration-300" style={{ width: `${calculateCompletion()}%` }} /></div>
              <p className="text-sm text-gray-600">{calculateCompletion()}% {t('petForm.profileCompletion')}</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4">
                <h4 className="font-semibold text-lg flex items-center">
                  <SafeIcon icon={FiClipboard} className="mr-3" />
                  {t('petForm.completeReviewTitle')}
                </h4>
              </div>

              <div className="p-4 md:p-6 space-y-6">
                <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6">
                  <div className="flex-shrink-0">
                    {formData.photo_url ? (
                      <img 
                        src={formData.photo_url} 
                        alt={formData.name} 
                        className="w-32 h-32 rounded-full object-cover border-4 border-gray-200 shadow-lg" 
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} 
                      />
                    ) : null}
                    <div className={`w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-200 ${formData.photo_url ? 'hidden' : 'flex'}`}>
                      <SafeIcon icon={FiUser} className="h-12 w-12 text-gray-400" />
                    </div>
                  </div>
                  
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                      {formData.name || t('petForm.unnamedPet')}
                    </h3>
                    
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-start space-x-2">
                          <span className="text-gray-500 font-medium min-w-[80px]">{t('petForm.type')}：</span>
                          <span className="text-gray-900">{petTypeOptions.find(p => p.value === formData.type)?.label || formData.type}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="text-gray-500 font-medium min-w-[80px]">{t('petForm.breed')}：</span>
                          <span className="text-gray-900">{formData.breed || t('petForm.notSpecified')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="text-gray-500 font-medium min-w-[80px]">{t('petForm.age')}：</span>
                          <span className="text-gray-900">{formData.birth_date ? formatAge(formData.birth_date) : t('petForm.notSpecified')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="text-gray-500 font-medium min-w-[80px]">{t('petForm.birthDate')}：</span>
                          <span className="text-gray-900">{formData.birth_date || t('petForm.notSpecified')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="text-gray-500 font-medium min-w-[80px]">{t('petForm.gender')}：</span>
                          <span className="text-gray-900">{formData.gender ? (formData.gender === 'male' ? t('petForm.male') : t('petForm.female')) : t('petForm.notSpecified')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="text-gray-500 font-medium min-w-[80px]">{t('petForm.weight')}：</span>
                          <span className="text-green-700 font-semibold">{formData.weight || t('petForm.notFilled')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="text-gray-500 font-medium min-w-[80px]">{t('petForm.height')}：</span>
                          <span className="text-orange-700 font-semibold">{formData.height || t('petForm.notFilled')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="text-gray-500 font-medium min-w-[80px]">{t('petForm.color')}：</span>
                          <TagList value={formData.color} color="indigo" emptyText={t('petForm.notFilled')} />
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="text-gray-500 font-medium min-w-[80px]">{t('petForm.currentLocation')}：</span>
                          <span className="text-gray-900">{formData.current_location || t('petForm.notFilled')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg border border-blue-100 overflow-hidden">
                    <button 
                      onClick={() => toggleSection('physical')}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-blue-100 transition-colors"
                    >
                      <h5 className="font-semibold text-blue-900 flex items-center">
                        <SafeIcon icon={FiUser} className="mr-2" />
                        {t('petForm.physicalTraits')}
                      </h5>
                      <SafeIcon 
                        icon={expandedSections.physical ? FiChevronUp : FiChevronDown} 
                        className="h-5 w-5 text-blue-700"
                      />
                    </button>
                    {expandedSections.physical && (
                      <div className="px-4 pb-4 space-y-2 text-sm">
                        <p><strong>{t('petForm.weight')}：</strong><span className="text-green-700 font-semibold">{formData.weight || t('petForm.notFilled')}</span></p>
                        <p><strong>{t('petForm.height')}：</strong><span className="text-orange-700 font-semibold">{formData.height || t('petForm.notFilled')}</span></p>
                        <div><strong>{t('petForm.color')}：</strong><TagList value={formData.color} color="indigo" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.coatType')}：</strong><TagList value={formData.coat_type} color="purple" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.eyeColor')}：</strong><TagList value={formData.eye_color} color="teal" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.bodySize')}：</strong><TagList value={formData.body_size} color="blue" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.specialMarkings')}：</strong><TagList value={formData.special_markings} color="pink" emptyText={t('petForm.notFilled')} /></div>
                        <p><strong>{t('petForm.microchipId')}：</strong>{formData.microchip_id || t('petForm.notFilled')}</p>
                        <p><strong>{t('petForm.registrationId')}：</strong>{formData.registration_id || t('petForm.notFilled')}</p>
                      </div>
                    )}
                  </div>

                  <div className="bg-red-50 rounded-lg border border-red-100 overflow-hidden">
                    <button 
                      onClick={() => toggleSection('dietary')}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-red-100 transition-colors"
                    >
                      <h5 className="font-semibold text-red-900 flex items-center">
                        <SafeIcon icon={FiHeart} className="mr-2" />
                        {t('petForm.dietaryInfo')}
                      </h5>
                      <SafeIcon 
                        icon={expandedSections.dietary ? FiChevronUp : FiChevronDown} 
                        className="h-5 w-5 text-red-700"
                      />
                    </button>
                    {expandedSections.dietary && (
                      <div className="px-4 pb-4 space-y-2 text-sm">
                        <p><strong>{t('petForm.feedingSchedule')}：</strong>{formData.feeding_schedule || t('petForm.notFilled')}</p>
                        {formData.feeding_plan && formData.feeding_plan.length > 0 && (
                          <div className="mt-2 pl-2 border-l-2 border-red-200">
                            {formData.feeding_plan.map(m => (
                              <p key={m.id} className="text-xs text-red-800">• {m.time} - {m.portion} {m.food && `(${m.food})`}</p>
                            ))}
                          </div>
                        )}
                        <div className="mt-2"><strong>{t('petForm.canEat')}：</strong><TagList value={formData.can_eat} color="green" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.cannotEat')}：</strong><TagList value={formData.cannot_eat} color="red" emptyText={t('petForm.notFilled')} /></div>
                      </div>
                    )}
                  </div>

                  <div className="bg-green-50 rounded-lg border border-green-100 overflow-hidden">
                    <button 
                      onClick={() => toggleSection('behavior')}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-green-100 transition-colors"
                    >
                      <h5 className="font-semibold text-green-900 flex items-center">
                        <SafeIcon icon={FiActivity} className="mr-2" />
                        {t('petForm.behaviorInfo')}
                      </h5>
                      <SafeIcon 
                        icon={expandedSections.behavior ? FiChevronUp : FiChevronDown} 
                        className="h-5 w-5 text-green-700"
                      />
                    </button>
                    {expandedSections.behavior && (
                      <div className="px-4 pb-4 space-y-2 text-sm">
                        <div><strong>{t('petForm.temperament')}：</strong><TagList value={formData.temperament} color="yellow" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.likes')}：</strong><TagList value={formData.likes} color="pink" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.dislikes')}：</strong><TagList value={formData.dislikes} color="orange" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.exerciseNeeds')}：</strong><TagList value={formData.exercise_needs} color="teal" emptyText={t('petForm.notFilled')} /></div>
                      </div>
                    )}
                  </div>

                  <div className="bg-purple-50 rounded-lg border border-purple-100 overflow-hidden">
                    <button 
                      onClick={() => toggleSection('health')}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-purple-100 transition-colors"
                    >
                      <h5 className="font-semibold text-purple-900 flex items-center">
                        <SafeIcon icon={FiShield} className="mr-2" />
                        {t('petForm.healthInfo')}
                      </h5>
                      <SafeIcon 
                        icon={expandedSections.health ? FiChevronUp : FiChevronDown} 
                        className="h-5 w-5 text-purple-700"
                      />
                    </button>
                    {expandedSections.health && (
                      <div className="px-4 pb-4 space-y-2 text-sm">
                        <p><strong>{t('petForm.medicationSchedule')}：</strong>{formData.medication_schedule || t('petForm.notFilled')}</p>
                        {formData.medication_plan && formData.medication_plan.length > 0 && (
                          <div className="mt-2 pl-2 border-l-2 border-purple-200">
                            {formData.medication_plan.map(m => (
                              <p key={m.id} className="text-xs text-purple-800">• {m.name} ({m.dosage}): {m.times?.join(', ')}</p>
                            ))}
                          </div>
                        )}
                        <div className="mt-2"><strong>{t('petForm.vaccines')}：</strong><TagList value={formData.vaccines} color="blue" emptyText={t('petForm.notFilled')} /></div>
                        <p><strong>{t('petForm.nextVetVisit')}：</strong>{formData.next_vaccine_date || t('petForm.notFilled')}</p>
                        <div><strong>{t('petForm.allergies')}：</strong><TagList value={formData.allergies} color="orange" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.medicalConditions')}：</strong><TagList value={formData.medical_conditions} color="red" emptyText={t('petForm.notFilled')} /></div>
                      </div>
                    )}
                  </div>

                  <div className="bg-orange-50 rounded-lg border border-orange-100 overflow-hidden">
                    <button 
                      onClick={() => toggleSection('vet')}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-orange-100 transition-colors"
                    >
                      <h5 className="font-semibold text-orange-900 flex items-center">
                        <SafeIcon icon={FiPhone} className="mr-2" />
                        {t('petForm.veterinarianInfo')}
                      </h5>
                      <SafeIcon 
                        icon={expandedSections.vet ? FiChevronUp : FiChevronDown} 
                        className="h-5 w-5 text-orange-700"
                      />
                    </button>
                    {expandedSections.vet && (
                      <div className="px-4 pb-4 space-y-2 text-sm">
                        <div><strong>{t('petForm.vetName')}：</strong><TagList value={formData.vet_name} color="orange" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.vetPhone')}：</strong><TagList value={formData.vet_phone} color="red" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.vetAddress')}：</strong><TagList value={formData.vet_address} color="blue" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.vetEmail')}：</strong><TagList value={formData.vet_email} color="purple" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.insuranceProvider')}：</strong><TagList value={formData.insurance_provider} color="blue" emptyText={t('petForm.notFilled')} /></div>
                        <div><strong>{t('petForm.insurancePolicy')}：</strong><TagList value={formData.insurance_policy} color="purple" emptyText={t('petForm.notFilled')} /></div>
                      </div>
                    )}
                  </div>

                  {formData.special_notes && (
                    <div className="bg-yellow-50 rounded-lg border border-yellow-100 overflow-hidden">
                      <button 
                        onClick={() => toggleSection('special')}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-yellow-100 transition-colors"
                      >
                        <h5 className="font-semibold text-yellow-900 flex items-center">
                          <SafeIcon icon={FiMessageSquare} className="mr-2" />
                          {t('petForm.specialNotes')}
                        </h5>
                        <SafeIcon 
                          icon={expandedSections.special ? FiChevronUp : FiChevronDown} 
                          className="h-5 w-5 text-yellow-700"
                        />
                      </button>
                      {expandedSections.special && (
                        <div className="px-4 pb-4">
                          <TagList value={formData.special_notes} color="yellow" emptyText={t('petForm.notFilled')} />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
                   {formState.savedPet && formState.savedPet.share_token ? (
                     <button onClick={handleShowShareModal} className="flex items-center justify-center space-x-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                       <SafeIcon icon={FiShare2} className="h-4 w-4" />
                       <span>{t('petForm.shareNow')}</span>
                     </button>
                   ) : (
                     <button onClick={handleSaveAndShare} disabled={formState.saving} className={`flex items-center justify-center space-x-2 px-6 py-3 rounded-lg transition-colors ${formState.saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`}>
                       <SafeIcon icon={formState.saving ? FiRotateCw : FiShare2} className={`h-4 w-4 ${formState.saving ? 'animate-spin' : ''}`} />
                       <span>{formState.saving ? t('petForm.saving') : t('petForm.saveAndShare')}</span>
                     </button>
                   )}
                  <button className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" onClick={() => { updateFormState({ currentStep: 1 }); setTimeout(() => scrollToTop(), 0); }}>
                    <SafeIcon icon={FiEdit3} className="h-4 w-4" />
                    <span>{t('petForm.editData')}</span>
                  </button>
                </div>
              </div>
            </div>
            <StepSaveButton />
          </div>
        )
      default: return null
    }
  }

  if (formState.loading) return (<div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-center"><div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-200 border-t-blue-600 mb-6 mx-auto"></div><p className="text-gray-600 text-lg font-medium">{t('common.loadingPet')}</p></div></div>)

  if (formState.previewMode) {
     const petTypeOptions = getPetTypeOptions()
     return (
        <div className="min-h-screen bg-gray-50 py-8">
           <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
             <div className="mb-6"><button onClick={handlePreviewToggle} className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 mb-4"><SafeIcon icon={FiArrowLeft} className="h-5 w-5" /><span>{t('petForm.backToEditMode')}</span></button></div>
             <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                 <div className="bg-blue-600 text-white px-6 py-4"><h2 className="text-2xl font-bold">{formData.name || t('petForm.unnamedPet')} {t('petForm.completeProfilePreview')}</h2></div>
                 <div className="p-6">
                    <div className="flex items-start space-x-6 mb-8">
                      <div className="flex-shrink-0">{formData.photo_url ? (<img src={formData.photo_url} alt={formData.name} className="w-32 h-32 rounded-full object-cover border-4 border-gray-200" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />) : <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center"><SafeIcon icon={FiUser} className="h-12 w-12 text-gray-400" /></div>}</div>
                      <div>
                         <h3 className="text-3xl font-bold text-gray-900 mb-4">{formData.name || t('petForm.unnamedPet')}</h3>
                         <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-gray-600">
                            <p><strong>{t('petForm.type')}：</strong>{petTypeOptions.find(p => p.value === formData.type)?.label || formData.type}</p>
                            <p><strong>{t('petForm.breed')}：</strong>{formData.breed || t('petForm.notSpecified')}</p>
                            <p><strong>{t('petForm.age')}：</strong>{formData.birth_date ? formatAge(formData.birth_date) : t('petForm.notSpecified')}</p>
                            <p><strong>{t('petForm.gender')}：</strong>{formData.gender ? (formData.gender === 'male' ? t('petForm.male') : t('petForm.female')) : t('petForm.notSpecified')}</p>
                            <p><strong>{t('petForm.currentLocation')}：</strong>{formData.current_location || t('petForm.notFilled')}</p>
                         </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                       <div className="bg-green-50 p-6 rounded-lg"><h4 className="font-semibold text-green-900 mb-4 text-lg">{t('petForm.dietaryInfo')}</h4><div className="space-y-3 text-sm"><p><strong>{t('petForm.feedingSchedule')}：</strong>{formData.feeding_schedule}</p>{formData.feeding_plan && formData.feeding_plan.map(m => <p key={m.id} className="text-xs ml-2">• {m.time} - {m.portion}</p>)}<div><strong>{t('petForm.canEat')}：</strong><TagList value={formData.can_eat} color="green" emptyText={t('petForm.notFilled')} /></div><div><strong>{t('petForm.cannotEat')}：</strong><TagList value={formData.cannot_eat} color="red" emptyText={t('petForm.notFilled')} /></div></div></div>
                        <div className="bg-blue-50 p-6 rounded-lg"><h4 className="font-semibold text-blue-900 mb-4 text-lg">{t('petForm.behaviorInfo')}</h4><div className="space-y-3 text-sm"><div><strong>{t('petForm.temperament')}：</strong><TagList value={formData.temperament} color="yellow" emptyText={t('petForm.notFilled')} /></div><div><strong>{t('petForm.likes')}：</strong><TagList value={formData.likes} color="pink" emptyText={t('petForm.notFilled')} /></div><div><strong>{t('petForm.dislikes')}：</strong><TagList value={formData.dislikes} color="orange" emptyText={t('petForm.notFilled')} /></div><div><strong>{t('petForm.exerciseNeeds')}：</strong><TagList value={formData.exercise_needs} color="teal" emptyText={t('petForm.notFilled')} /></div></div></div>
                        <div className="bg-purple-50 p-6 rounded-lg"><h4 className="font-semibold text-purple-900 mb-4 text-lg">{t('petForm.healthInfo')}</h4><div className="space-y-3 text-sm"><p><strong>{t('petForm.medicationSchedule')}：</strong>{formData.medication_schedule}</p>{formData.medication_plan && formData.medication_plan.map(m => <p key={m.id} className="text-xs ml-2">• {m.name}: {m.times?.join(', ')}</p>)}<div><strong>{t('petForm.vaccines')}：</strong><TagList value={formData.vaccines} color="blue" emptyText={t('petForm.notFilled')} /></div><p><strong>{t('petForm.nextVetVisit')}：</strong>{formData.next_vaccine_date}</p><div><strong>{t('petForm.allergies')}：</strong><TagList value={formData.allergies} color="orange" emptyText={t('petForm.notFilled')} /></div><div><strong>{t('petForm.medicalConditions')}：</strong><TagList value={formData.medical_conditions} color="red" emptyText={t('petForm.notFilled')} /></div></div></div>
                        <div className="bg-orange-50 p-6 rounded-lg">
                            <h4 className="font-semibold text-orange-900 mb-4 text-lg">{t('petForm.veterinarianInfo')}</h4>
                            <div className="space-y-3 text-sm">
                                <div><strong>{t('petForm.vetName')}：</strong><TagList value={formData.vet_name} color="orange" emptyText={t('petForm.notFilled')} /></div>
                                <div><strong>{t('petForm.vetPhone')}：</strong><TagList value={formData.vet_phone} color="red" emptyText={t('petForm.notFilled')} /></div>
                                <div><strong>{t('petForm.vetAddress')}：</strong><TagList value={formData.vet_address} color="blue" emptyText={t('petForm.notFilled')} /></div>
                                <div><strong>{t('petForm.vetEmail')}：</strong><TagList value={formData.vet_email} color="purple" emptyText={t('petForm.notFilled')} /></div>
                                <div><strong>{t('petForm.insuranceProvider')}：</strong><TagList value={formData.insurance_provider} color="blue" emptyText={t('petForm.notFilled')} /></div>
                                <div><strong>{t('petForm.insurancePolicy')}：</strong><TagList value={formData.insurance_policy} color="purple" emptyText={t('petForm.notFilled')} /></div>
                            </div>
                        </div>
                    </div>
                    {formData.special_notes && (<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6"><h4 className="font-semibold text-yellow-900 mb-3 text-lg">{t('petForm.specialNotes')}</h4><TagList value={formData.special_notes} color="yellow" emptyText={t('petForm.notFilled')} /></div>)}
                 </div>
             </div>
           </div>
        </div>
     )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <button onClick={handleBackToDashboard} className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"><SafeIcon icon={FiArrowLeft} className="h-5 w-5" /><span>{t('petForm.back')}</span></button>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => updateFormState({ showHelpTips: !formState.showHelpTips })} 
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${formState.showHelpTips ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}
              >
                <SafeIcon icon={FiHelpCircle} className="h-4 w-4" />
                <span>{formState.showHelpTips ? t('petForm.hideTips') : t('petForm.showTips')}</span>
              </button>
              <button onClick={() => updateFormState({ previewMode: true })} className="flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm text-gray-500 hover:bg-gray-100 transition-colors"><SafeIcon icon={FiEye} className="h-4 w-4" /><span>{t('petForm.preview')}</span></button>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden" ref={formContainerRef}>
            <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 px-8 py-6">
              <div className="flex items-center justify-between text-white mb-6">
                <h2 className="text-2xl font-bold">{isEdit ? t('petForm.editPet') : t('petForm.createPetProfile')}</h2>
                <div className="flex items-center space-x-2 text-white/90 text-sm">
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">{t('petForm.step')} {formState.currentStep} / {totalSteps}</span>
                </div>
              </div>
              
              <div className="w-full bg-white/20 backdrop-blur-sm rounded-full h-2 mb-8">
                <div className="bg-white h-2 rounded-full transition-all duration-500 ease-out shadow-[0_0_10px_rgba(255,255,255,0.5)]" style={{ width: `${(formState.currentStep / totalSteps) * 100}%` }} />
              </div>
              
              <div className="hidden md:flex justify-between relative px-2">
                {stepTitles.map((title, index) => {
                  const stepNum = index + 1
                  const isActive = stepNum === formState.currentStep
                  const isCompleted = stepNum < formState.currentStep
                  return (
                    <div key={index} className="flex flex-col items-center relative z-10 cursor-pointer group" onClick={() => handleStepChange(stepNum)}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 font-bold mb-2 ${isActive ? 'bg-white text-indigo-600 border-white scale-110 shadow-lg' : isCompleted ? 'bg-white/80 text-indigo-600 border-white/80 hover:bg-white hover:scale-105' : 'bg-white/30 text-white border-white/50 hover:bg-white/40 hover:scale-105'}`}>
                        {isCompleted ? <SafeIcon icon={FiCheck} className="h-5 w-5" /> : stepNum}
                      </div>
                      <span className={`text-xs font-medium transition-colors duration-300 ${isActive ? 'text-white' : isCompleted ? 'text-white/90' : 'text-white/70'}`}>{title}</span>
                    </div>
                  )
                })}
              </div>

               <div className="flex md:hidden justify-center items-center space-x-2 relative px-2">
                {stepTitles.map((title, index) => {
                    const stepNum = index + 1
                    const isActive = stepNum === formState.currentStep
                    const isCompleted = stepNum < formState.currentStep
                    
                    return (
                        <div 
                            key={index} 
                            className={`transition-all duration-300 rounded-full cursor-pointer ${
                                isActive 
                                    ? 'w-8 h-2.5 bg-white shadow-md' 
                                    : isCompleted 
                                        ? 'w-2.5 h-2.5 bg-white/80 hover:bg-white hover:scale-125' 
                                        : 'w-2.5 h-2.5 bg-white/40 hover:bg-white/60 hover:scale-125'
                            }`}
                            onClick={() => handleStepChange(stepNum)}
                            title={title}
                        />
                    )
                })}
              </div>

            </div>
            
            <div className="p-6 md:p-8">
              <AnimatePresence mode="wait">
                <motion.div key={formState.currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                  {renderStepContent()}
                </motion.div>
              </AnimatePresence>
              
              <div className="flex justify-between items-center mt-12 pt-6 border-t border-gray-100">
                <button onClick={handlePrevious} disabled={formState.currentStep === 1} className={`flex items-center px-6 py-2.5 rounded-lg transition-colors font-medium ${formState.currentStep === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}><SafeIcon icon={FiArrowLeft} className="mr-2 h-4 w-4" />{t('petForm.previous')}</button>
                <div className="flex items-center space-x-4">
                  {formState.currentStep < totalSteps ? (
                    <button onClick={handleNext} className="flex items-center px-8 py-2.5 rounded-lg shadow-md transition-all font-medium bg-indigo-600 hover:bg-indigo-700 text-white hover:shadow-lg hover:-translate-y-0.5"><span>{t('petForm.next')}</span><SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></button>
                  ) : (
                    <button onClick={handleSave} disabled={formState.saving} className={`flex items-center px-8 py-2.5 rounded-lg shadow-md transition-all font-medium ${formState.saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white hover:shadow-lg hover:-translate-y-0.5'}`}><SafeIcon icon={formState.saving ? FiRotateCw : FiCheck} className={`mr-2 h-4 w-4 ${formState.saving ? 'animate-spin' : ''}`} /><span>{formState.saving ? t('petForm.saving') : t('petForm.complete')}</span></button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {successNotification.isVisible && (<SuccessNotification title={successNotification.title} message={successNotification.message} type={successNotification.type} actions={successNotification.actions} onClose={() => setSuccessNotification(prev => ({ ...prev, isVisible: false }))} />)}
      {formState.showShareModal && (<ShareModal pet={formState.savedPet || formData} isOpen={formState.showShareModal} onClose={() => updateFormState({ showShareModal: false })} />)}
    </div>
  )
}

export default PetForm